import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { ApiError } from '@/api/client'
import * as enterpriseApi from '@/api/enterprise'
import type { EnterpriseBinding } from '@/api/enterprise'
import { useAuthStore } from '@/stores/auth'

/**
 * The tenant's Google enterprise binding.
 *
 * Binding is irreversible and happens once per customer, so this is read far
 * more often than it changes: the router asks whether a page that needs a bound
 * enterprise may open, and the settings page shows the detail. Both read the
 * same cached answer.
 *
 * `bound` is null until the first read settles. The guard treats null as "do not
 * know yet" and lets the navigation through, because redirecting on unknown
 * state would send every cold load to onboarding.
 */

/** Google's binding lock is 300s, so there is no point polling past a minute. */
const POLL_INTERVAL_MS = 3000
const POLL_ATTEMPTS = 20

function isAlreadyBound(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 409 && error.errors.code?.[0] === 'already_bound'
  )
}

export const useEnterpriseStore = defineStore('enterprise', () => {
  const binding = ref<EnterpriseBinding | null>(null)
  const loading = ref(false)
  const pollExhausted = ref(false)

  let inFlight: Promise<void> | null = null
  let pollTimer: ReturnType<typeof setTimeout> | null = null

  const bound = computed<boolean | null>(() => binding.value?.bound ?? null)
  const signupPending = computed(() => binding.value?.signup_pending === true)
  /** When the outstanding signup URL expires, or null when none is outstanding. */
  const pendingSignupExpiresAt = computed(() =>
    signupPending.value ? (binding.value?.signup_url_expires_at ?? null) : null,
  )

  /**
   * No session means no one to ask for. Operators have no tenant and so no
   * enterprise, and a tenant user without `devices.read` would earn a 403 on
   * every navigation. Either way there is nothing to fetch.
   */
  function readableTenantId(): string | null {
    const auth = useAuthStore()
    if (!auth.isAuthenticated) return null
    const tenantId = auth.user?.tenant_id ?? null
    if (tenantId === null || auth.user?.realm !== 'tenant') return null
    return auth.can('devices.read') ? tenantId : null
  }

  async function load(force = false): Promise<void> {
    if (binding.value !== null && !force) return
    if (inFlight !== null) return inFlight

    const tenantId = readableTenantId()
    if (tenantId === null) return

    loading.value = true
    inFlight = (async () => {
      try {
        binding.value = await enterpriseApi.fetchBinding(tenantId)
      } catch {
        // Left null so the guard stays permissive and the next read retries.
        // A status page that cannot load says so; it must not block the app.
      } finally {
        loading.value = false
        inFlight = null
      }
    })()

    return inFlight
  }

  /**
   * Resolves to null when the tenant turns out to be bound already. A
   * double-clicked button sends the second request after the first has bound
   * the tenant: that is the success path arriving twice, not a fault.
   */
  async function startSignup(): Promise<enterpriseApi.SignupUrl | null> {
    const auth = useAuthStore()
    const tenantId = auth.user?.tenant_id
    if (tenantId == null) throw new Error('No tenant on the current session.')

    try {
      return await enterpriseApi.createSignupUrl(tenantId)
    } catch (error) {
      if (!isAlreadyBound(error)) throw error
      markBound()
      return null
    }
  }

  /**
   * Re-reads the binding until it is bound or the attempts run out. Used by the
   * callback page when Google returns `in_progress`: another request holds the
   * binding lock, so the answer is coming, just not yet.
   */
  function pollUntilBound(): void {
    stopPolling()
    pollExhausted.value = false
    let attempts = 0

    const tick = async (): Promise<void> => {
      attempts += 1
      await load(true)
      if (bound.value === true) return
      if (attempts >= POLL_ATTEMPTS) {
        pollExhausted.value = true
        return
      }
      pollTimer = setTimeout(() => void tick(), POLL_INTERVAL_MS)
    }

    pollTimer = setTimeout(() => void tick(), POLL_INTERVAL_MS)
  }

  function stopPolling(): void {
    if (pollTimer !== null) clearTimeout(pollTimer)
    pollTimer = null
  }

  /** After a successful bind, so the next read does not serve the stale answer. */
  function markBound(): void {
    if (binding.value !== null) binding.value.bound = true
  }

  return {
    binding,
    loading,
    pollExhausted,
    bound,
    pendingSignupExpiresAt,
    load,
    startSignup,
    pollUntilBound,
    stopPolling,
    markBound,
  }
})
